import React from 'react';
import { Link } from 'react-router-dom';
import { PPEData } from '../types/ppe';

interface CategoriesPageProps {
  data: PPEData;
}

const CategoriesPage: React.FC<CategoriesPageProps> = ({ data }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-4">
            <Link to="/" className="hover:text-gray-900 transition-colors duration-200">Home</Link>
            <span>/</span>
            <span className="text-black font-medium">Categories</span>
          </nav>
          
          <div className="text-center">
            <h1 className="text-4xl font-bold text-black mb-4">PPE Categories & Risk Levels</h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Explore all available protection categories and understand EU risk classifications.
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          
          {/* PPE Product Categories */}
          <div>
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-black mb-2">Certificates by Product Category</h2>
              <p className="text-gray-600">
                Browse different types of personal protective equipment and their certification requirements.
              </p>
            </div>
            
            <div className="space-y-4">
              {data.categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/category/${category.id}`}
                  className="block bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow duration-200 group"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center overflow-hidden rounded">
                      {category.certificates.length > 0 && 
                       category.certificates[0].products && 
                       category.certificates[0].products.length > 0 ? (
                        <img 
                          src={category.certificates[0].products[0].imageUrl} 
                          alt={category.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-gray-600 font-semibold text-lg">
                          {category.name.charAt(0)}
                        </span>
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-black group-hover:text-gray-700 transition-colors duration-200">
                        {category.name}
                      </h3>
                      <p className="text-sm text-gray-600">{category.description}</p>
                      <div className="mt-2 text-xs text-gray-500">
                        {category.certificates.length} certificate{category.certificates.length !== 1 ? 's' : ''} available
                      </div>
                    </div>
                    <div className="text-gray-400 group-hover:text-gray-600 transition-colors duration-200">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Risk Levels */}
          <div>
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-black mb-2">Risk Level Classifications</h2>
              <p className="text-gray-600">
                Understanding EU PPE risk categories helps you choose the right protection level.
              </p>
            </div>
            
            <div className="space-y-4">
              {Object.entries(data.riskLevels).map(([level, info]) => (
                <div key={level} className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-gray-700 font-bold text-lg">{level}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-black mb-1">{info.name}</h3>
                      <p className="text-sm text-gray-600">{info.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Additional Info */}
            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">About EU Risk Categories</h4>
              <p className="text-sm text-blue-800">
                EU Regulation (EU) 2016/425 categorizes PPE by risk level. Category I covers minimal risks, 
                Category II covers intermediate risks, and Category III covers high/severe risks. 
                Higher categories require more rigorous testing and certification procedures.
              </p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-12 text-center">
          <div className="inline-flex flex-col sm:flex-row gap-4">
            <Link to="/search" className="btn-primary">
              Search All Certificates
            </Link>
            <Link to="/" className="btn-secondary">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoriesPage;
